import { useState } from "react";
import { useTranslation } from "react-i18next";
import { withCollapsedContext, type DiffLine } from "../../../shared/diff/lineDiff";
import { changeToneClass } from "../../styles/changeStyles";
import { classNames } from "../../styles/classNames";
import { ChangeMark } from "../ChangeRow";

/** Unchanged lines shown next to each change; longer unchanged runs fold. */
const CONTEXT_LINES = 3;

/** A diff line with its numbers in the old and new text (`null` where it is not in one). */
interface NumberedLine extends DiffLine {
  readonly oldNumber: number | null;
  readonly newNumber: number | null;
}

function numbered(lines: readonly DiffLine[]): readonly NumberedLine[] {
  let oldNumber = 0;
  let newNumber = 0;
  return lines.map((line) => {
    if (line.kind !== "added") oldNumber += 1;
    if (line.kind !== "removed") newNumber += 1;
    return { ...line, oldNumber: line.kind === "added" ? null : oldNumber, newNumber: line.kind === "removed" ? null : newNumber };
  });
}

/**
 * The light comparison for lists and history rows: old and new line numbers, each change
 * marked by sign, colour and word, unchanged runs folded behind a button that shows them,
 * in a bounded block that scrolls with a visible scrollbar.
 */
export default function InlineDiff({ lines, label }: { lines: readonly DiffLine[]; label: string }) {
  const { t } = useTranslation();
  const [opened, setOpened] = useState<ReadonlySet<number>>(new Set());
  const rows = withCollapsedContext(numbered(lines), CONTEXT_LINES);
  return (
    <div className="app-code-panel app-source-scroll max-h-96 py-1 font-mono text-xs" role="group" aria-label={label} tabIndex={0}>
      {rows.map((row) => {
        if (row.kind !== "collapsed") return <Line key={`${String(row.oldNumber)}:${String(row.newNumber)}`} line={row} />;
        if (opened.has(row.start)) return row.lines.map((line) => <Line key={`${String(line.oldNumber)}:${String(line.newNumber)}`} line={line} />);
        return (
          <button
            key={`fold:${String(row.start)}`}
            type="button"
            className={classNames(changeToneClass("unchanged"), "block w-full px-2 py-0.5 text-left italic hover:underline")}
            onClick={() => { setOpened(new Set(opened).add(row.start)); }}
          >
            {t("common.source.showUnchanged", { count: row.lines.length })}
          </button>
        );
      })}
    </div>
  );
}

function Line({ line }: { line: NumberedLine }) {
  return (
    <p className={classNames("grid grid-cols-[2.5rem_2.5rem_minmax(0,1fr)] px-2", line.kind !== "unchanged" && changeToneClass(line.kind))}>
      <span aria-hidden={true} className="app-text-muted select-none pr-2 text-right">{line.oldNumber ?? ""}</span>
      <span aria-hidden={true} className="app-text-muted select-none pr-2 text-right">{line.newNumber ?? ""}</span>
      <span className="whitespace-pre-wrap break-words">
        <ChangeMark kind={line.kind} />
        {line.text === "" ? " " : line.text}
      </span>
    </p>
  );
}
